import { CreateCommentRequest, GetCommentResponse, IntegrationException, LoginOrigin, OrderedComment } from '../types';
import { LitElement, PropertyValues, css, html } from 'lit';
import { createInternalChallengeNote, createPointComment, deleteComment, update_comment } from '../helpers/api/comments-api';
import { customElement, property, query, state } from 'lit/decorators';
import { getFromLocalStorage, isDefined, removeLocalStorage, setLocalStorage, undefinedIfEmpty } from '../helpers/utils';
import { login, upgradeToken } from '../helpers/api/account-api';

import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { getRelativeTime } from '../helpers/date';
import pointCache from '../helpers/state/point-cache';
import { random } from '@ctrl/tinycolor';
import topicData from '../helpers/state/topic-data';
import { getMainPage } from '../helpers/page-name';

@customElement('comment-component')
export class CommentComponent extends LitElement {
    static amplitude_callsite: string = 'Comment Modal';

    @property({ type: Boolean })
    showEmailConfirm: boolean = false;

    @query('#email')
    emailComponent?: HTMLInputElement;

    @query('#username')
    usernameComponent?: HTMLInputElement;

    @query('#commentText')
    commentComponent?: HTMLInputElement;

    @query('#publicSwitch')
    publicSwitch?: HTMLInputElement;

    @state()
    title: string = '';

    @state()
    step: string = '';

    @state()
    emailAddress?: string;

    @state()
    oneTimeCode?: string;

    @state()
    orderedComments: OrderedComment[] = [];

    @state()
    allComments: GetCommentResponse[] = [];

    @state()
    editingComment?: GetCommentResponse;

    @state()
    parentCommentId?: string;

    @state()
    focusedCommentId?: string;

    @state()
    entity_id?: string;

    @state()
    username: string | undefined = accountInfo.account?.full_name ?? getFromLocalStorage('username') ?? undefined;

    @state()
    activated_challenge_key?: string;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountInfo = new StateController(this, accountInfo);
    private readonly bindChallengeData = new StateController(this, challengeData);

    // Getter to dynamically check if we're in internal note mode
    get isInternalNote(): boolean {
        return challengeData.challenge?.allow_internal_notes === true &&
               challengeData.activatedChallengeKey !== undefined;
    }

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        this.step = 'view_comment_section';
        this.title = `${this.commentNamePlural}`;

        const commentMap = new Map<string, OrderedComment>();

        // Create a map of comments by their ID
        this.allComments.forEach((comment) => {
            commentMap.set(comment._id?.$oid, {
                id: comment._id?.$oid,
                comment,
                children: [],
            });
        });

        // Group comments by their parent ID
        this.allComments.forEach((comment) => {
            if (comment.parent_comment_id) {
                const parentComment = commentMap.get(comment.parent_comment_id.$oid);
                if (parentComment) {
                    parentComment.children.push({
                        id: comment._id?.$oid,
                        comment,
                        children: [],
                    });
                } else {
                    // Handle missing parent comment
                    commentMap.set(comment.parent_comment_id.$oid, {
                        id: random().toString(),
                        comment: {
                            _id: comment.parent_comment_id,
                            content: 'This comment has been removed or is private',
                            email: '',
                            created_at: '',
                            is_public: false,
                            parent_comment_id: undefined,
                            username: 'Unknown',
                            edited: false,
                            is_comment_writer: false,
                            is_entity_writer: false,
                            updated_at: '',
                        },
                        children: [{
                            id: comment._id?.$oid,
                            comment,
                            children: [],
                        }],
                    });
                }
            }
        });

        // Filter out only top-level comments
        this.orderedComments = Array.from(commentMap.values()).filter((c) => !c.comment.parent_comment_id);

        this.username = undefinedIfEmpty(accountInfo.account?.full_name) ?? getFromLocalStorage('username') ?? undefined;
    }

    // if all comment is updated, update ordered comments
    protected async updated(_changedProperties: PropertyValues) {
        if (_changedProperties.has('allComments')) {
            await this.startup();
        }
    }

    signIn() {
            if (!accountInfo.isAuthenticated && this.showEmailConfirm) {
        const signin_modal_election_status = getFromLocalStorage('signin_modal_election');
        if (isDefined(signin_modal_election_status)) {
            const status = JSON.parse(signin_modal_election_status);
            this.emailAddress = status.email ?? '';

            this.title = 'Confirm Email';
            this.step = 'signin_confirm';
        }
    }
    }

    onChangeEmailAddress(e: any) {
        this.emailAddress = e.detail.value;
    }

    onChangeOneTimeCode(e: any) {
        this.oneTimeCode = e.detail.value;
    }

    async handleAddComment(parentId: string | undefined) {
        this.parentCommentId = parentId;
        // For internal notes in challenges, skip email verification
        if (!accountInfo.isAuthenticated && !this.isInternalNote) {
            this.showEmailConfirm = true;
            this.handleClickSignin();
            return;
        }
        this.title = `Add ${this.commentNameSingular}`;
        this.step = 'add_comment_section';
    }

    async handleEditComment(commentId: string) {
        const comment = this.allComments.find((c) => c._id?.$oid === commentId);
        if (!comment) {
            return;
        }

        this.editingComment = comment;

        this.parentCommentId = comment.parent_comment_id?.$oid;

        this.title = `Edit ${this.commentNameSingular}`;
        this.step = 'add_comment_section';
    }

    async handleDeleteComment(commentId: string) {
        if (!confirm('Are you sure you want to delete this comment? This action cannot be undone.')) {
            return;
        }
        if (!this.isInternalNote) {
            await deleteComment(commentId);
        } else {
            await challengeData.deleteInternalNoteForChallengePoint(commentId);
        }
        this.dispatchEvent(new CustomEvent('comment-changed'));
    }

    handleClickSignin() {
        this.title = `Verify Your Email to Add a ${this.commentNameSingular}`;
        this.step = 'signin_email';
    }

    private async handleEmailSubmit() {
        const email = this.emailComponent?.value ?? '';

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await login(CommentComponent.amplitude_callsite, email, LoginOrigin.Comment, topicData.topic?.name ?? '');
            this.title = 'Confirm Email';
            this.step = 'signin_confirm';
            this.requestUpdate();
        }, 'Oops! That doesn’t look like a valid email address.  Try again.');
    }

    private handleTryNewEmail() {
        removeLocalStorage('signin_modal_election');
        this.emailAddress = '';
        this.title =  `Verify Your Email to Add a ${this.commentNameSingular}`;
        this.step = 'signin_email';
    }

    private async handleSubmitOneTimeCode() {
        removeLocalStorage('signin_modal_election');

        const email = this.emailAddress ?? '';
        const oneTimeCode = this.oneTimeCode ?? '';

        const username = this.username ?? this.usernameComponent?.value ?? undefined;

        if (isDefined(username)) {
            setLocalStorage('username', username);
        }

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            // Authenticate in the backend with one time code
            await upgradeToken(
                CommentComponent.amplitude_callsite,
                email,
                oneTimeCode,
                undefined,
                LoginOrigin.Comment
            );
            await accountInfo.instantiateAccountData(false);

            this.title = `Add ${this.commentNameSingular}`;
            this.step = 'add_comment_section';

        }, 'Hmm... That code isn’t right. Try a resending the code or entering a different email address.');
    }

    private async handleSubmitComment() {
        const comment = this.commentComponent?.value ?? '';
        const username = this.username ?? this.usernameComponent?.value ?? undefined;

        if (isDefined(username)) {
            setLocalStorage('username', username);
        }

        const isPublic = this.publicSwitch?.checked ?? true;

        try {
            // Use internal notes API for challenge participants
            if (this.isInternalNote && isDefined(challengeData.activatedChallengeKey)) {
                const username = undefinedIfEmpty(challengeData.activated_challenge?.display_name) ?? undefinedIfEmpty(this.username) ?? undefinedIfEmpty(challengeData.activated_challenge?.email);
                await createInternalChallengeNote(this.entity_id ?? '', comment, challengeData.activatedChallengeId ?? '', challengeData.activatedChallengeKey ?? '', username);
            } else {
                await createPointComment(this.entity_id ?? '', comment, isPublic, this.parentCommentId, username);
            }
            this.title = 'Comments';
            this.step = 'view_comment_section';
        } catch (error) {
            console.error('error submitting comment', error);
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            getMainPage().errorToastElement.showToastAndThrow(`There was an error submitting your comment. Please try again later.`);
        }

        this.dispatchEvent(new CustomEvent('comment-changed'));
    }

    private async handleUpdateComment() {
        const comment_details = this.editingComment;
        if (!comment_details) {
            return;
        }
        const comment = this.commentComponent?.value ?? '';
        const username = this.username ?? this.usernameComponent?.value ?? undefined;

        if (isDefined(username)) {
            setLocalStorage('username', username);
        }
        if (!this.isInternalNote) {
            const isPublic = this.publicSwitch?.checked ?? true;

            const comment_request: CreateCommentRequest = {
                _id: this.editingComment?._id,
                content: comment,
                is_public: isPublic,
                username: username ?? comment_details?.username ?? '',
                parent_comment_id: comment_details?.parent_comment_id,
                send_notification: false,
            };

            try {
                await update_comment(this.editingComment?._id?.$oid ?? '', comment_request);
                this.title = 'Comments';
                this.step = 'view_comment_section';
            } catch (error) {
                console.error('error submitting comment', error);
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                getMainPage().errorToastElement.showToastAndThrow(`There was an error submitting your comment. Please try again later.`);
            }
        } else {
            try {
                await challengeData.updateInternalNoteForChallengePoint(this.editingComment?._id?.$oid ?? '', comment);
                this.title = 'Comments';
                this.step = 'view_comment_section';
            } catch (error) {
                console.error('error updating internal note', error);
                getMainPage().errorToastElement.showToastAndThrow(`There was an error updating your note. Please try again later.`);
            }
        }
        this.editingComment = undefined;

        this.dispatchEvent(new CustomEvent('comment-changed'));
    }

    get canReply() {
        return topicData.topic?.comment_settings?.allow_comment_replies === true;
    }

    renderComment(comment: OrderedComment) {
        const commentData = comment;
        const children = comment.children;

        return html`
            <div class="comment-wrapper">
                <div class="comment">
                    <div class="comment__header">
                        <div class="comment__author">${commentData.comment.username ?? ''} ${commentData.comment.is_comment_writer   ? '(You)' : ''}</div>
                        <div class="comment__date">
                            ${getRelativeTime(commentData.comment.created_at)}
                            <br>
                            <div class="comment__actions">
                                ${this.canReply ? html`
                                    <div class="comment__action" @click="${async () => await this.handleAddComment(commentData.comment._id?.$oid)}">Reply</div>
                                ` : ''}
                                ${commentData.comment.is_comment_writer ? html`
                                    <i><div class="comment__action action_admin" @click="${async () => await this.handleEditComment(commentData.comment._id?.$oid)}">
                                        Edit
                                    </div></i>` : ''}
                                ${commentData.comment.is_entity_writer || commentData.comment.is_comment_writer ? html`
                                    <i><div class="comment__action action_admin" @click="${async () => await this.handleDeleteComment(commentData.comment._id?.$oid)}">
                                        Delete
                                    </div></i>` : ''}
                            </div>
                        </div>
                    </div>
                    <div class="comment__content">
                        <i>${commentData.comment.is_public ? '' : `🔒(Private) `}</i>
                        ${commentData.comment.content}
                    </div>

                </div>
                <div class="comment__replies">
                    ${children.map((child) => html`
                         <div class="comment__reply">
                            <div class="comment__header">
                                <div class="reply__author">${child.comment.username ?? ''} ${child.comment.is_comment_writer ? '(You)' : ''}</div>
                                <div class="comment__date">${getRelativeTime(child.comment.created_at)}</div>
                            </div>
                            <div class="comment__content">
                                <i>${child.comment.is_public ? '' : `🔒 (Private) `}</i>
                                ${child.comment.content}
                            </div>
                            <div class="comment__actions">
                                ${child.comment.is_comment_writer ? html`
                                    <i><div class="comment__action action_admin" @click="${async () => await this.handleEditComment(child.comment._id?.$oid)}">
                                        Edit
                                    </div></i>` : ''}
                                ${child.comment.is_entity_writer || child.comment.is_comment_writer ? html`
                                    <i><div class="comment__action action_admin" @click="${async () => await this.handleDeleteComment(child.comment._id?.$oid)}">
                                        Delete
                                    </div></i>` : ''}
                            </div>
                        </div>
                    `)}
                </div>
            </div>
        `;
    }

    get commentNameSingular() {
        return topicData.topic?.comment_settings?.comment_title?.singular ?? 'Comment';
    }

    get commentNamePlural() {
        return topicData.topic?.comment_settings?.comment_title?.plural ?? 'Comments';
    }

    get commentInstructions() {
        return topicData.topic?.comment_settings?.comment_instructions ?? '';
    }

    get isRoswell(): boolean {
        return topicData.hasExceptions === IntegrationException.Roswell;
    }

    private renderByStep() {
        switch (this.step) {
            case 'view_comment_section':
                return html`
                    <span class="add_comment" @click="${async () => await this.handleAddComment(undefined)}">
                        New ${this.commentNameSingular}
                    </span>
                    <div class="voting-wrapper">
                        <div class="comment-section">
                            ${this.orderedComments.map((comment) => html`
                                ${this.renderComment(comment)}
                            `)}
                        </div>
                    </div>
                `;
            case 'signin_email':
                return html`
                    <div class="voting-wrapper">
                        <form-input
                            class="input__email-address"
                            id="email"
                            type="email"
                            label="Email Address"
                            placeholder=""
                            value="${this.emailAddress ?? ''}"
                            @change="${this.onChangeEmailAddress}"
                        >
                        </form-input>
                        <form-button
                            size="xs"
                            class="action-button__send-code"
                            @click="${async () => await this.handleEmailSubmit()}"
                        >
                            Send Code
                        </form-button>
                    </div>
                `;
            case 'signin_confirm':
                return html`
                    <div class="voting-wrapper">
                        <p>
                            Enter the code sent to <b>${this.emailAddress ?? ''}</b>.
                        </p>
                        <form-input
                            class="input__confirm-code"
                            id="code"
                            type="string"
                            label="Access Code"
                            placeholder="000000"
                            value="${this.oneTimeCode ?? ''}"
                            @change="${this.onChangeOneTimeCode}"
                        >
                        </form-input>
                        <form-button
                            size="xs"
                            class="action-button__confirm"
                            ?disabled="${!this.oneTimeCode}"
                            @click="${this.handleSubmitOneTimeCode}"
                        >
                            Continue Adding ${this.commentNameSingular}
                        </form-button>
                        <div class="link-buttons">
                            <p class="link-btn" @click=${this.handleTryNewEmail}>
                                Try Another Email
                            </p>
                            <p class="link-btn" @click=${this.handleEmailSubmit}>
                                Send a New Code
                            </p>
                        </div>
                    </div>
                `;
            case 'add_comment_section':
                return html`
                    <div class="voting-wrapper add-comment">
                        ${undefinedIfEmpty(this.commentInstructions) ? html`
                            <p class="description">${this.commentInstructions}</p>
                        ` : ''}
                        <proxi-textarea
                            id="commentText"
                            label="${this.commentNameSingular}"
                            outlined
                            placeholder="${this.isRoswell ? 'Enter your rating here, 1-5 (5 = best!) ⭐️' : 'Enter your comment here'}"
                            value=${this.editingComment?.content ?? ''}
                            ></proxi-textarea>
                        ${!isDefined(undefinedIfEmpty(this.username)) && !this.isInternalNote ? html`
                            <proxi-textfield
                                id="username"
                                label="Username"
                                outlined
                                placeholder="Enter your name"
                                value=${undefinedIfEmpty(accountInfo.account?.full_name) ?? getFromLocalStorage('username')}
                            ></proxi-textfield>
                        ` : this.isInternalNote ? '' : html`
                            <p class="description">You are writing as <span class="email-address"><i>${this.username}</i></span><span class="link-btn" @click=${() => {this.username = undefined;}}>Change</span></p>
                        `}
                        ${!this.isInternalNote ? html`
                            <div style="display: flex; align-items: center; justify-content: flex-end;flex-wrap:wrap;">
                                <form-switch
                                    id="publicSwitch"
                                    name="makePublicSwitch"
                                    style="flex: unset;"
                                    ?checked="${this.editingComment?.is_public ?? true}">
                                </form-switch>
                                <span class="additional infoText" style="margin-left: 0.2rem;">Make my ${this.commentNameSingular} public</span>
                            </div>
                            ${this.publicSwitch?.checked === true ? html`
                                <div class="description">Your ${this.commentNameSingular} will be public</div>
                            ` : html`
                                <div class="description">Only the map owner and the person who added the point will see your comment</div>
                            `}
                        ` : html`
                            <div class="description">🔒 ${this.isRoswell ? 'This rating is private (only you and the Roswell team can see it)' : 'This note is private'}
                        `}
                        <div class="actions-wrapper">
                            <form-button
                                variant="outline"
                                size="xs"
                                class="action-button primary-blue"
                                @click="${() => {
                                    this.title = 'Comments';
                                    this.step = 'view_comment_section';
                                }}"
                            > Back
                            </form-button>
                            ${this.editingComment ? html`
                                <form-button
                                    size="xs"
                                    class="action-button primary-blue"
                                    @click="${async () => await this.handleUpdateComment()}"
                                >
                                    Save Changes
                                </form-button>
                            ` : html`
                                <form-button
                                    size="xs"
                                    class="action-button primary-blue"
                                    @click="${async () => await this.handleSubmitComment()}"
                                >
                                    Submit ${this.commentNameSingular}
                                </form-button>
                            `}
                        </div>
                    </div>
                `;
                ;
            default:
                return '';
        }
    }



    render() {
        return html`
            <div class="modalContent">
                <div class="modal__header">${this.title}</div>
                <div class="modal__content">${this.renderByStep()}</div>
                <div class="close-button-wrapper">
                    <mwc-icon
                        @click="${() => this.dispatchEvent(new CustomEvent('close'))}"
                    >close</mwc-icon>
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                flex: 1 1 auto;
                overflow-y: auto;
            }

            .modalContent {
                position: relative;
                height: 100%;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                overflow-y: auto;
                align-items: center;
            }

                        /* width */
            .modalContent::-webkit-scrollbar{
                width: 5px;
            }

            /* Track */
            .modalContent::-webkit-scrollbar-track{
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .modalContent::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .modal__header {
                width: 100%;
                padding: 0px 20px 5px;
                font-size: 1rem;
                font-weight: 700;
                line-height: 1em;
                box-sizing: border-box;
                text-align:center;
            }

            .modal__content {
                padding: 5px 0;
                width: 100%;
                box-sizing: border-box;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                overflow-x: clip;
            }

            .direction-button-wrapper {
                display: flex;
                flex-direction:row;
                align-items: center;
                gap: 5px;
            }

            .selection-option {
                display: flex;
                justify-content: flex-start;
                gap: 5px;
                align-items: center;
                border-bottom: 1px solid var(--theme-border-color);
                padding:5px 10px;
                cursor: pointer;
            }

            .close-button-wrapper {
                position: absolute;
                top: -5px;
                right: 0px;
                cursor: pointer;
            }

            .selection-option:hover {
                font-weight:700;
                background-color: var(--theme-border-color);
                cursor: pointer;
            }

            .select-category-option{
                width: calc(25% - 20px);
                display: flex;
                flex-direction: column;
                height: 50px;
                background-color: var(--theme-border-color);
                box-shadow: 0 0 5px 0 rgba(0,0,0,0.1);
                cursor: pointer;
                padding: 5px;
                align-items: center;
                justify-content: center;
                line-height: 1;
                text-align: center;
            }

            .complete_category {
                background-color: var(--main-success-color);
                color: var(--theme-white-color);
                box-shadow: unset;
            }

            .select-category-option:hover {
                background-color: var(--main-sky-color);
            }

            .selection-wrapper {
                flex: 1;
                overflow-y: auto;
                width:100%;
            }

            .select-category-wrapper {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                width: 100%;
                align-items: center;
                justify-content: flex-start;
                gap: 10px;
                overflow-y: auto;
                flex: 1;
            }

            .comment-section {
                display: flex;
                flex-direction: column;
                overflow: auto;
                width: 100%;
            }

            .comment__actions {
                display: flex;
                flex-direction: row;
                gap: 10px;
            }

            .direction-buttons {
                display: flex;
                align-items: center;
                width: 100%;
                gap: 5px;
                justify-content: center;
                flex-wrap: wrap;
                padding-top: 10px;
                border-top : 1px solid var(--main-primary-color);
            }

            .direction-button{
                white-space: nowrap;
            }

            .column {
                flex-direction: column;
            }

            .actions-wrapper {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
                gap: 5px;
                Width: 100%;
            }

            .add-comment {
                padding: 5px;
            }

            .circle-button {
                font-size: 12px;
                border-radius: 50%;
                border: 1px black solid;
                height: 24px;
                width: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
            }

            .circle-button-placeholder {
                height: 24px;
                width: 24px;
            }

            .circle-button mwc-icon {
                font-size: 14px;
            }

            .primary-blue {
                --btn-background-color: var(--main-primary-color);
                --mdc-theme-primary: var(--main-primary-color);
            }

            .failure-red {
                --mdc-theme-primary: var(--main-invalid-color);
            }

            .terms__desc-wrapper {
                overflow-y: auto;
                box-sizing: border-box;
                line-height: 130%;
                color: var(--theme-input-color);
                margin: 20px 0;
                flex: 1;
                text-align: center;
            }

            .terms__checkbox-wrapper {
                width: 100%;
                box-sizing: border-box;
                margin: 10px 0;
                text-align: center;
            }

            #electionAgreeement {
                --form-checkbox-label-color: var(--theme-input-color);
            }

            p {
                text-align: center;
                margin: 5px 0;
            }

            .description {
                width: 100%;
                color: var(--theme-input-color);
                margin-bottom: 10px;
                margin-top: 5px;
                text-align: center;
                display: flex;
                flex-direction: row;
                justify-content: center;
                gap: 5px;
                align-items: center;
            }

            .category-description {
                font-size: 0.75rem;
                font-weight: 700;
                margin-bottom: 10px;
                text-align: center;
            }

            .selection-recorded {
                font-size: 0.75rem;
                color: var(--main-invalid-color);
                margin-bottom: 10px;
                text-align: center;
            }

            .voting-wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                overflow-y: auto;
            }

            .flexContainer {
                display: flex;
            }

            .justifySpaceBetween {
                justify-content: space-between;
            }

            .link-buttons {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                margin-top: 20px;
            }

            .link-btn {
                cursor: pointer;
                text-decoration: underline;
                line-height: 125%;
                color: var(--theme-input-color);
            }

            .email-address {
                color: var(--theme-input-color);
            }

            .action-button__send-code {
                width: 100%;
                max-width: 240px;
            }

            .action-button__confirm {
                width: 100%;
                max-width: 250px;
                margin: 15px 0;
            }

            .input__email-address {
                width: 100%;
                max-width: 240px;
                margin-top: 15px;
                text-align: center;
            }

            .input__confirm-code,
            .input__user-name {
                width: 100%;
                max-width: 250px;
                margin: 5px 0;
                text-align: center;
            }

            .comment-wrapper {
                border: 1px solid #ddd;
                padding: 5px 10px;
                margin-bottom: 10px;
                border-radius: 5px;
                background-color: white;
            }

            .comment {
            }

            .add_comment {
                cursor: pointer;
                color: var(--main-primary-color);
                text-decoration: underline;
                margin-bottom: 10px;
                text-align: center;
            }

            .comment__header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 5px;
            }

            .comment__author {
                font-weight: bold;
                color: #333;
            }

            .reply__author {
                font-weight: bold;
                color: #333;
                font-size: 0.7rem;
            }

            .comment__date {
                font-size: 0.9em;
                color: #999;
                float: right;
                font-size: 0.65rem;
            }

            .comment__content {
                margin-bottom: 5px;
                color: #555;
            }

            .comment__action {
                cursor: pointer;
                color: var(--main-success-color);
                text-decoration: underline;
                text-align: right;
                font-size: 0.8rem;
            }

            .action_admin {
                color: var(--main-invalid-color);
            }

            .comment__action:hover {
                text-decoration: none;
            }

            .comment__replies {
                margin-left: 20px;
                border-left: 2px solid #ddd;
                padding-left: 10px;
                margin-top: 10px;
            }

            .comment__reply {
                margin-bottom: 10px;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                    padding: 0;
                }
                .select-category-option{
                    width: calc(50% - 20px);
                }

                .modal__header {
                    font-size: 14px;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}


